import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarDays, Plus, Loader2, Sparkles, Users, Heart, PartyPopper, Coffee, Trophy, CheckCircle, XCircle, Eye, Wallet, Settings2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_TYPES = [
  { value: 'appreciation', label: 'Appreciation', icon: Heart, color: 'text-rose-500' },
  { value: 'team_bonding', label: 'Team Bonding', icon: Users, color: 'text-blue-500' },
  { value: 'flexible_break', label: 'Flexible Break', icon: Coffee, color: 'text-amber-500' },
  { value: 'wellness', label: 'Wellness', icon: Sparkles, color: 'text-emerald-500' },
  { value: 'custom', label: 'Other', icon: PartyPopper, color: 'text-purple-500' },
];

const STATUS_BADGE = {
  planned: { label: 'Planned', cls: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
  completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 line-through' },
};

const ProgramsPage = () => {
  const { isHR } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [participantsModal, setParticipantsModal] = useState(null);
  const [completeModal, setCompleteModal] = useState(null); // { program, participants, awards: [{employee_id, name, dept, award, bonus}] }
  const [budget, setBudget] = useState(null);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ quarterly_cap: 500, alert_threshold_pct: 80 });
  const [form, setForm] = useState({
    title: '', description: '', type: 'appreciation',
    scheduled_date: new Date().toISOString().slice(0, 10),
    duration_minutes: 60, witties_reward: 5, status: 'planned',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [programsRes, summaryRes, budgetRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
        isHR ? axios.get(`${API_URL}/api/programs/participation/summary`, { withCredentials: true }) : Promise.resolve(null),
        axios.get(`${API_URL}/api/program-budget`, { withCredentials: true }),
      ]);
      if (programsRes.status === 'fulfilled') setPrograms(programsRes.value.data);
      if (summaryRes && summaryRes.status === 'fulfilled' && summaryRes.value) setSummary(summaryRes.value.data);
      if (budgetRes.status === 'fulfilled') {
        setBudget(budgetRes.value.data);
        setBudgetForm({ quarterly_cap: budgetRes.value.data.quarterly_cap, alert_threshold_pct: budgetRes.value.data.alert_threshold_pct });
      }
    } finally { setLoading(false); }
  }, [isHR]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveBudget = async () => {
    try {
      await axios.put(`${API_URL}/api/program-budget`, budgetForm, { withCredentials: true });
      toast.success('Budget updated');
      setBudgetDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.scheduled_date) { toast.error('Title and date are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/programs`, form, { withCredentials: true });
      toast.success('Program created!');
      setCreateOpen(false);
      setForm({ title: '', description: '', type: 'appreciation', scheduled_date: new Date().toISOString().slice(0, 10), duration_minutes: 60, witties_reward: 5, status: 'planned' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create program'); }
    finally { setSubmitting(false); }
  };

  const handleJoin = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/programs/${id}/join`, {}, { withCredentials: true });
      toast.success(res.data.joined ? "You're in! 🎉" : 'Removed from participants');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleComplete = async (id) => {
    // Fetch participants and open the bonus-award dialog
    try {
      const res = await axios.get(`${API_URL}/api/programs/${id}/participants`, { withCredentials: true });
      setCompleteModal({
        program: res.data.program,
        participants: res.data.participants,
        awards: res.data.participants.map((p) => ({
          employee_id: p.employee_id,
          employee_name: p.employee_name,
          department: p.department,
          award: true,
          bonus: 0,
        })),
      });
    } catch (err) { toast.error('Failed to load participants'); }
  };

  const handleConfirmComplete = async () => {
    if (!completeModal) return;
    try {
      const res = await axios.post(`${API_URL}/api/programs/${completeModal.program.id}/complete`, {
        awards: completeModal.awards.map((a) => ({ employee_id: a.employee_id, award: a.award, bonus: a.bonus })),
      }, { withCredentials: true });
      toast.success(`Program completed — ${res.data.witties_awarded_to} participant(s), ${res.data.total_witties_distributed} Witties total.`);
      setCompleteModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this program?')) return;
    try {
      await axios.delete(`${API_URL}/api/programs/${id}`, { withCredentials: true });
      toast.success('Program cancelled');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleViewParticipants = async (program) => {
    try {
      const res = await axios.get(`${API_URL}/api/programs/${program.id}/participants`, { withCredentials: true });
      setParticipantsModal(res.data);
    } catch (err) { toast.error('Failed to load participants'); }
  };

  const typeMeta = (k) => PROGRAM_TYPES.find(t => t.value === k) || PROGRAM_TYPES[4];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const upcoming = programs.filter(p => p.status === 'planned' || p.status === 'active');
  const past = programs.filter(p => p.status === 'completed' || p.status === 'cancelled');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="programs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><PartyPopper className="w-7 h-7 text-fuchsia-500" /> Engagement Programs</h1>
          <p className="text-muted-foreground">Monthly appreciation, team bonding & flexible breaks. Earn Witties for participating.</p>
        </div>
        {isHR && (
          <Button className="gap-2" onClick={() => setCreateOpen(true)} data-testid="new-program-btn"><Plus className="w-4 h-4" /> New Program</Button>
        )}
      </div>

      {/* Surprise & Delight quarterly Witties budget */}
      {budget && (
        <Card data-testid="budget-card" className={`${budget.is_breach ? 'border-red-300 dark:border-red-900/50' : budget.is_warning ? 'border-amber-300 dark:border-amber-900/50' : 'border-emerald-200 dark:border-emerald-900/40'}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${budget.is_breach ? 'bg-red-100 dark:bg-red-950/30' : budget.is_warning ? 'bg-amber-100 dark:bg-amber-950/30' : 'bg-emerald-100 dark:bg-emerald-950/30'}`}>
                  <Wallet className={`w-5 h-5 ${budget.is_breach ? 'text-red-600' : budget.is_warning ? 'text-amber-600' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className="font-semibold flex items-center gap-2">Surprise & Delight — {budget.quarter.label}
                    {budget.is_breach && <Badge variant="outline" className="text-[10px] gap-1 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300"><AlertTriangle className="w-3 h-3" /> Cap reached</Badge>}
                    {budget.is_warning && !budget.is_breach && <Badge variant="outline" className="text-[10px] gap-1 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"><AlertTriangle className="w-3 h-3" /> {budget.alert_threshold_pct}% used</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">Witties spent through engagement programs this quarter</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Spent / Cap</p>
                  <p className="font-mono font-semibold text-base"><span data-testid="budget-spent">{budget.spent}</span> / <span data-testid="budget-cap">{budget.quarterly_cap}</span> 🏆</p>
                  <p className="text-xs text-muted-foreground">Remaining: <span className="font-mono" data-testid="budget-remaining">{budget.remaining}</span></p>
                </div>
                {isHR && <Button size="sm" variant="outline" onClick={() => setBudgetDialogOpen(true)} className="gap-1" data-testid="budget-config-btn"><Settings2 className="w-4 h-4" /> Configure</Button>}
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2.5 rounded-full bg-muted overflow-hidden" data-testid="budget-progress">
              <div className={`h-full transition-all duration-500 ${budget.is_breach ? 'bg-red-500' : budget.is_warning ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, budget.percentage)}%` }} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">{budget.percentage}% used · alert at {budget.alert_threshold_pct}% · resets every quarter</p>
          </CardContent>
        </Card>
      )}

      {/* HR Summary */}
      {isHR && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="programs-summary">
          <SummaryTile label="Active employees" value={summary.total_active_employees} />
          <SummaryTile label="Programs (last 20)" value={summary.programs.length} />
          <SummaryTile label="Avg participation"
            value={`${summary.programs.length > 0 ? Math.round(summary.programs.reduce((a, p) => a + p.participation_rate, 0) / summary.programs.length) : 0}%`} />
          <SummaryTile label="Feedback responses" value={summary.feedback_responses_total} />
        </div>
      )}

      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming" className="gap-2"><CalendarDays className="w-4 h-4" /> Upcoming ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="past" className="gap-2"><Trophy className="w-4 h-4" /> Past ({past.length})</TabsTrigger>
          {isHR && <TabsTrigger value="participation" className="gap-2"><Users className="w-4 h-4" /> Participation</TabsTrigger>}
        </TabsList>

        <TabsContent value="upcoming" className="mt-4 space-y-3">
          {upcoming.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              No upcoming programs. {isHR ? 'Create one to boost morale!' : 'HR will schedule programs soon.'}
            </CardContent></Card>
          ) : upcoming.map((p) => <ProgramCard key={p.id} p={p} typeMeta={typeMeta} isHR={isHR} onJoin={handleJoin} onComplete={handleComplete} onCancel={handleCancel} onViewParticipants={handleViewParticipants} />)}
        </TabsContent>

        <TabsContent value="past" className="mt-4 space-y-3">
          {past.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">No past programs yet.</CardContent></Card>
          ) : past.map((p) => <ProgramCard key={p.id} p={p} typeMeta={typeMeta} isHR={isHR} onJoin={handleJoin} onComplete={handleComplete} onCancel={handleCancel} onViewParticipants={handleViewParticipants} />)}
        </TabsContent>

        {isHR && (
          <TabsContent value="participation" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Programs participation</CardTitle>
                <CardDescription>Recent 20 programs · % of active employees</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead><tr><th>Title</th><th>Type</th><th>Date</th><th>Status</th><th>Participants</th><th>%</th></tr></thead>
                  <tbody>
                    {summary?.programs?.map(row => (
                      <tr key={row.id}>
                        <td>{row.title}</td>
                        <td className="capitalize text-xs">{row.type?.replace('_', ' ')}</td>
                        <td className="text-xs">{row.scheduled_date}</td>
                        <td><Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[row.status]?.cls || ''}`}>{STATUS_BADGE[row.status]?.label || row.status}</Badge></td>
                        <td className="text-center">{row.participants}</td>
                        <td className="text-center font-mono">{row.participation_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Surveys participation</CardTitle>
                <CardDescription>Last 10 pulse surveys & feedback forms</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead><tr><th>Question</th><th>Type</th><th>Anonymous</th><th>Status</th><th>Responses</th><th>%</th></tr></thead>
                  <tbody>
                    {summary?.surveys?.map(row => (
                      <tr key={row.id}>
                        <td className="max-w-xs truncate">{row.question}</td>
                        <td className="capitalize text-xs">{row.type}</td>
                        <td>{row.anonymous_strict ? <span className="text-purple-600">🔒 Strict</span> : <span className="text-muted-foreground">Anonymous</span>}</td>
                        <td><Badge variant="outline" className="text-[10px]">{row.status}</Badge></td>
                        <td className="text-center">{row.votes}</td>
                        <td className="text-center font-mono">{row.participation_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Program Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PartyPopper className="w-5 h-5" /> New Engagement Program</DialogTitle><DialogDescription>Give your team something to look forward to.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {PROGRAM_TYPES.map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.value} type="button" onClick={() => setForm({ ...form, type: t.value })}
                      className={`p-2 rounded-lg border text-center transition-colors ${form.type === t.value ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                      data-testid={`program-type-${t.value}`}>
                      <Icon className={`w-5 h-5 mx-auto ${t.color}`} />
                      <p className="text-[10px] mt-1">{t.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., October Appreciation Tea" data-testid="program-title" /></div>
            <div className="space-y-1.5"><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What's special about this one?" data-testid="program-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Scheduled Date</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="program-date" /></div>
              <div className="space-y-1.5"><Label>Duration (min)</Label><Input type="number" min="15" max="480" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Witties reward per participant <span className="text-muted-foreground text-xs">(0 = no reward)</span></Label>
              <Input type="number" min="0" max="100" value={form.witties_reward} onChange={(e) => setForm({ ...form, witties_reward: parseInt(e.target.value) || 0 })} data-testid="program-witties" />
              <p className="text-xs text-muted-foreground">Auto-awarded when you mark the program "Completed".</p>
            </div>
            <div className="space-y-1.5">
              <Label>Initial Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="active">Active (open for joining now)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full gap-2" onClick={handleCreate} disabled={submitting} data-testid="program-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />} Create Program
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Participants Modal */}
      <Dialog open={!!participantsModal} onOpenChange={(o) => !o && setParticipantsModal(null)}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid="participants-modal">
          <DialogHeader>
            <DialogTitle>{participantsModal?.program?.title}</DialogTitle>
            <DialogDescription>{participantsModal?.count || 0} participant(s)</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(participantsModal?.participants || []).length > 0 ? participantsModal.participants.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-2 border rounded-md text-sm">
                <div>
                  <p className="font-medium">{p.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{p.department || '—'} · joined {new Date(p.joined_at).toLocaleString('en-IN')}</p>
                </div>
                {p.witties_awarded > 0 && <Badge className="text-xs bg-amber-100 text-amber-800">+{p.witties_awarded} 🏆</Badge>}
              </div>
            )) : <p className="text-center text-sm text-muted-foreground py-4">No participants yet.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete & Award Modal — pick MVP recipients with bonus Witties */}
      <Dialog open={!!completeModal} onOpenChange={(o) => !o && setCompleteModal(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="complete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Complete & Award — {completeModal?.program?.title}</DialogTitle>
            <DialogDescription>
              Base Witties: <span className="font-semibold">+{completeModal?.program?.witties_reward || 0}</span> per included participant.
              Add an MVP bonus (+2 / +5 / +10) for stand-outs, or uncheck to exclude.
            </DialogDescription>
          </DialogHeader>
          {completeModal && (
            <div className="space-y-3">
              {/* Bulk-toggle helpers */}
              <div className="flex items-center gap-2 text-xs">
                <Button size="sm" variant="outline" className="h-7" onClick={() => setCompleteModal({ ...completeModal, awards: completeModal.awards.map(a => ({ ...a, award: true })) })} data-testid="select-all-award">Select all</Button>
                <Button size="sm" variant="outline" className="h-7" onClick={() => setCompleteModal({ ...completeModal, awards: completeModal.awards.map(a => ({ ...a, award: false })) })} data-testid="select-none">Clear all</Button>
                <Button size="sm" variant="outline" className="h-7" onClick={() => setCompleteModal({ ...completeModal, awards: completeModal.awards.map(a => ({ ...a, bonus: 0 })) })}>Reset bonuses</Button>
              </div>

              {completeModal.awards.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-6">No participants joined.</p>
              ) : (
                <div className="space-y-2">
                  {completeModal.awards.map((a, idx) => {
                    const total = a.award ? (completeModal.program.witties_reward || 0) + a.bonus : 0;
                    return (
                      <div key={a.employee_id} className={`flex items-center gap-3 p-2.5 rounded-md border ${a.award ? '' : 'opacity-50 bg-muted/30'}`} data-testid={`award-row-${idx}`}>
                        <input
                          type="checkbox"
                          checked={a.award}
                          onChange={(e) => {
                            const awards = [...completeModal.awards];
                            awards[idx] = { ...a, award: e.target.checked };
                            setCompleteModal({ ...completeModal, awards });
                          }}
                          className="w-4 h-4"
                          data-testid={`award-check-${idx}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{a.employee_name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{a.department || '—'}</p>
                        </div>
                        {/* Bonus stepper */}
                        <div className="flex gap-1" role="group" aria-label="MVP bonus">
                          {[0, 2, 5, 10].map((b) => (
                            <button
                              key={b}
                              type="button"
                              disabled={!a.award}
                              onClick={() => {
                                const awards = [...completeModal.awards];
                                awards[idx] = { ...a, bonus: b };
                                setCompleteModal({ ...completeModal, awards });
                              }}
                              className={`px-2 py-1 rounded text-xs font-medium border transition-colors disabled:opacity-40 ${a.bonus === b ? 'border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'hover:bg-muted'}`}
                              data-testid={`award-bonus-${idx}-${b}`}
                            >
                              {b === 0 ? 'Base' : `+${b}`}
                            </button>
                          ))}
                        </div>
                        <Badge variant="outline" className="text-xs w-16 justify-center" data-testid={`award-total-${idx}`}>
                          {a.award ? `+${total}` : '—'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Summary */}
              <div className="flex items-center justify-between p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 text-sm">
                <span>
                  <span className="font-semibold">{completeModal.awards.filter((a) => a.award).length}</span> recipient(s) ·{' '}
                  <span className="font-semibold">{completeModal.awards.filter((a) => a.award && a.bonus > 0).length}</span> with MVP bonus
                </span>
                <span className="font-mono font-semibold" data-testid="award-total-witties">
                  Total Witties: {completeModal.awards.reduce((s, a) => s + (a.award ? (completeModal.program.witties_reward || 0) + a.bonus : 0), 0)} 🏆
                </span>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCompleteModal(null)} data-testid="award-cancel-btn">Cancel</Button>
                <Button onClick={handleConfirmComplete} className="gap-2" data-testid="award-confirm-btn">
                  <Trophy className="w-4 h-4" /> Complete & Award
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Budget config dialog */}
      <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Surprise & Delight Budget</DialogTitle>
            <DialogDescription>Cap quarterly Witties spend across all engagement programs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Quarterly cap (Witties)</Label>
              <Input type="number" min="0" max="100000" value={budgetForm.quarterly_cap} onChange={(e) => setBudgetForm({ ...budgetForm, quarterly_cap: parseInt(e.target.value) || 0 })} data-testid="budget-cap-input" />
              <p className="text-xs text-muted-foreground">Set to 0 to disable enforcement (just track spend).</p>
            </div>
            <div className="space-y-1.5">
              <Label>Warning threshold (% of cap)</Label>
              <Input type="number" min="0" max="100" value={budgetForm.alert_threshold_pct} onChange={(e) => setBudgetForm({ ...budgetForm, alert_threshold_pct: parseInt(e.target.value) || 0 })} data-testid="budget-threshold-input" />
              <p className="text-xs text-muted-foreground">Progress bar turns amber once spend crosses this %.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBudgetDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveBudget} data-testid="budget-save-btn" className="gap-2"><Wallet className="w-4 h-4" /> Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryTile = ({ label, value }) => (
  <Card><CardContent className="p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-xl font-bold mt-1">{value}</p>
  </CardContent></Card>
);

const ProgramCard = ({ p, typeMeta, isHR, onJoin, onComplete, onCancel, onViewParticipants }) => {
  const meta = typeMeta(p.type);
  const Icon = meta.icon;
  const statusInfo = STATUS_BADGE[p.status] || { label: p.status, cls: '' };
  const isPast = p.status === 'completed' || p.status === 'cancelled';
  return (
    <Card data-testid={`program-${p.id}`} className={p.status === 'cancelled' ? 'opacity-70 border-dashed' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Icon className={`w-6 h-6 ${meta.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h3 className="font-semibold">{p.title}</h3>
                <p className="text-xs text-muted-foreground capitalize">{meta.label} · {p.scheduled_date} · {p.duration_minutes}m</p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${statusInfo.cls}`}>{statusInfo.label}</Badge>
            </div>
            {p.description && <p className="text-sm mt-2 text-muted-foreground">{p.description}</p>}
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <Badge variant="secondary" className="text-xs gap-1"><Users className="w-3 h-3" /> {p.participants_count} joined</Badge>
              {p.witties_reward > 0 && <Badge className="text-xs gap-1 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">🏆 +{p.witties_reward} Witties</Badge>}
              <div className="ml-auto flex gap-2 flex-wrap">
                {!isPast && (
                  <Button size="sm" variant={p.i_joined ? 'outline' : 'default'} onClick={() => onJoin(p.id)} data-testid={`join-${p.id}`} className="gap-1">
                    {p.i_joined ? <><CheckCircle className="w-3 h-3" /> Joined — leave</> : <><Plus className="w-3 h-3" /> Join</>}
                  </Button>
                )}
                {isHR && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => onViewParticipants(p)} data-testid={`view-participants-${p.id}`} className="gap-1"><Eye className="w-3 h-3" /> Roster</Button>
                    {!isPast && p.participants_count > 0 && (
                      <Button size="sm" variant="outline" onClick={() => onComplete(p.id)} data-testid={`complete-${p.id}`} className="gap-1 text-emerald-700"><Trophy className="w-3 h-3" /> Complete & Award</Button>
                    )}
                    {!isPast && (
                      <Button size="sm" variant="ghost" onClick={() => onCancel(p.id)} data-testid={`cancel-${p.id}`} className="gap-1 text-red-700"><XCircle className="w-3 h-3" /></Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgramsPage;
